import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChartOfAccountsData } from '../../pages/SetupWizardPage'

interface Step2ChartOfAccountsProps {
  initialData: ChartOfAccountsData | null
  onSubmit: (data: ChartOfAccountsData) => void
  onBack: () => void
  loading: boolean
}

const TEMPLATES = [
  {
    id: 'association_basic' as const,
    name: 'Yhdistys - Perus',
    description: 'Simple chart of accounts for small associations',
    accounts: 9,
    features: ['Perustilit', 'Jäsenmaksutulot', 'Toiminta- ja hallintokulut'],
  },
  {
    id: 'association_extended' as const,
    name: 'Yhdistys - Laajennettu',
    description: 'Extended chart of accounts for more complex operations',
    accounts: 18,
    features: ['Useita pankkitilejä', 'Avustus- ja lahjoitustulot', 'Tarkemmat kulutilit'],
  },
  {
    id: 'company' as const,
    name: 'Yritys',
    description: 'Yrityksen tilikartta',
    accounts: 6,
    features: ['Myyntitulot', 'Materiaalit ja palvelut', 'Yritystilit'],
  },
  {
    id: 'custom' as const,
    name: 'Mukautettu',
    description: 'Create your own chart of accounts later',
    accounts: 0,
    features: ['Täysi kontrolli', 'Luo tilit itse', 'Vie aikaa enemmän'],
  },
]

export default function Step2ChartOfAccounts({
  initialData,
  onSubmit,
  onBack,
  loading,
}: Step2ChartOfAccountsProps) {
  const { t } = useTranslation()
  const [selectedTemplate, setSelectedTemplate] = useState<ChartOfAccountsData['template']>(
    initialData?.template || 'association_basic'
  )
  const [showPreview, setShowPreview] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      template: selectedTemplate,
    })
  }

  const selectedTemplateData = TEMPLATES.find(t => t.id === selectedTemplate)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Tilikartan valinta</h2>
      <p className="text-gray-600 mb-6">Valitse organisaatiollesi sopiva tilikarttapohja. Voit muokata tilejä myöhemmin.</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEMPLATES.map((template) => (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`
                p-4 border-2 rounded-xl cursor-pointer transition-all
                ${selectedTemplate === template.id
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{template.name}</h3>
                {selectedTemplate === template.id && (
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3">{template.description}</p>
              <div className="text-xs text-gray-500 mb-2">
                {template.accounts > 0 ? `${template.accounts} tiliä` : 'Mukautettu'}
              </div>
              <ul className="space-y-1">
                {template.features.map((feature, idx) => (
                  <li key={idx} className="text-xs text-gray-500 flex items-center">
                    <span className="mr-1.5">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Preview button */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {showPreview ? t('setup.hidePreview') : t('setup.showPreview')}
          </button>
        </div>

        {/* Preview */}
        {showPreview && selectedTemplateData && selectedTemplateData.id !== 'custom' && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Tilikartan sisältö</h4>
            <div className="text-sm text-gray-600 space-y-1">
              {selectedTemplateData.id === 'association_basic' && (
                <>
                  <div className="font-medium text-gray-700 mt-2">Varat:</div>
                  <div className="pl-2">1000 Käteisvarat</div>
                  <div className="pl-2">1900 Pankkisaamiset</div>
                  <div className="font-medium text-gray-700 mt-2">Velat:</div>
                  <div className="pl-2">2000 Ostovelat</div>
                  <div className="pl-2">2900 Siirtovelat</div>
                  <div className="font-medium text-gray-700 mt-2">Tulot:</div>
                  <div className="pl-2">3000 Jäsenmaksutulot</div>
                  <div className="pl-2">3100 Muut tulot</div>
                  <div className="font-medium text-gray-700 mt-2">Menot:</div>
                  <div className="pl-2">4000 Toimintakulut</div>
                  <div className="pl-2">4100 Hallintokulut</div>
                  <div className="font-medium text-gray-700 mt-2">Oma pääoma:</div>
                  <div className="pl-2">9000 Edellisten kausien ylijäämä/alijäämä</div>
                </>
              )}
              {selectedTemplateData.id === 'association_extended' && (
                <>
                  <div className="font-medium text-gray-700 mt-2">Varat:</div>
                  <div className="pl-2">1000 Käteisvarat, 1100 Pankkitili 1, 1200 Pankkitili 2</div>
                  <div className="pl-2">1300 Saamiset jäseniltä, 1900 Muut saamiset</div>
                  <div className="font-medium text-gray-700 mt-2">Velat:</div>
                  <div className="pl-2">2000 Ostovelat, 2100 Jäsenmaksusaamiset</div>
                  <div className="pl-2">2900 Siirtovelat</div>
                  <div className="font-medium text-gray-700 mt-2">Tulot:</div>
                  <div className="pl-2">3000 Jäsenmaksutulot, 3100 Avustustulot</div>
                  <div className="pl-2">3200 Lahjoitustulot, 3300 Muut tulot</div>
                  <div className="font-medium text-gray-700 mt-2">Menot:</div>
                  <div className="pl-2">4000 Toimintakulut, 4100 Hallintokulut</div>
                  <div className="pl-2">4200 Markkinointikulut, 4300 Tilavuokrat</div>
                  <div className="pl-2">4400 Muut kulut</div>
                </>
              )}
              {selectedTemplateData.id === 'company' && (
                <>
                  <div className="pl-2">1000 Käteisvarat, 1900 Pankkisaamiset</div>
                  <div className="pl-2">2000 Ostovelat</div>
                  <div className="pl-2">3000 Myyntitulot</div>
                  <div className="pl-2">4000 Materiaalit ja palvelut</div>
                  <div className="pl-2">9000 Edellisten kausien tulos</div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Info about custom template */}
        {selectedTemplate === 'custom' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-amber-600 mr-2">⚠️</span>
              <div>
                <h4 className="font-medium text-amber-900">Mukautettu tilikartta</h4>
                <p className="text-sm text-amber-800 mt-1">
                  Valitsemalla mukautetun tilikartan luot tilit itse myöhemmin asetuksissa.
                  Tämä vaatii enemmän aikaa mutta antaa täyden kontrollin.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            ← Takaisin
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Tallennetaan...
              </>
            ) : (
              <>Seuraava →</>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
